import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  useAddActionNoteMutation,
  useLoginUserWithTvCodeMutation,
  useRecoverUserMutation,
  useResetDeviceAllMutation,
  useResetUserPasswordMutation,
  useSendPasswordResetLinkMutation,
  useSendUserLoginRequestMutation,
} from "../../../../store/api/userManagementApi.js";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  FormGroup,
  Form,
  Card,
  CardBody,
  Row,
  Col,
} from "reactstrap";
import "../styles.css";
import AddCommentModal from "../shared/add_note.model.jsx";
import StreakManagementModal from "../shared/streak_manage.model.jsx";
import { service } from "./service.jsx";

const UserSupport = () => {
  console.log("Rendering support page")
  const userData = useSelector(state => state.userManagement.userData) || {};
  const user_id = userData?.user_id;
  const email = userData?.user_email;
  const userDelete = userData?.delete_status;
  const displayEmail = email || "—";
  const [code, setCode] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showResetAllModal, setShowResetAllModal] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [currentActionType, setCurrentActionType] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [showStreakManagement, setShowStreakManagement] = useState(false);
  const [showRecoveryConfirmModal, setShowRecoveryConfirmModal] = useState(false);
  const [addActionNote] = useAddActionNoteMutation();
  const [resetUserPassword] = useResetUserPasswordMutation();
  const [loginUserWithTvCode] = useLoginUserWithTvCodeMutation();
  const [sendPasswordResetLink] = useSendPasswordResetLinkMutation();
  const [sendUserLoginRequest] = useSendUserLoginRequestMutation();
  const [resetDeviceAll] = useResetDeviceAllMutation();
  const [recoverUser] = useRecoverUserMutation();

  const getPublisherConfigInfo = keyName => {
    let response = false;
    if (
      localStorage.getItem("publisherConfig") &&
      typeof localStorage.getItem("publisherConfig") !== "undefined" &&
      localStorage.getItem("publisherConfig") !== "undefined"
    ) {
      var publisherConfig = JSON.parse(localStorage.getItem("publisherConfig"));
      if (publisherConfig) {
        let configInfo = publisherConfig.filter((i) => keyName == i.key_name);
        response = configInfo[0] && configInfo[0].key_value;
        return response;
      }
    }
    return response;
  };

  const handleChangePassword = () => {
    setShowChangePassword(true);
    setNewPassword("");
    setPasswordError("");
  };

  const handleCommentSubmit = async (note) => {
    try {
      const commentData = {
        action_type: currentActionType,
        note: note,
        user_id
      };
      
      // Submit note (success or failure doesn't matter for main flow)
      await addActionNote(commentData).unwrap();
      
      // Close note modal and proceed with original action
      setIsCommentModalOpen(false);
      if (pendingAction) {
        pendingAction();
      }
    } catch (error) {
      console.error('Comment submission error:', error);
      // Still proceed with action even if note fails
      setIsCommentModalOpen(false);
      if (pendingAction) {
        pendingAction();
      }
    } finally {
      setPendingAction(null);
      setCurrentActionType("");
    }
  };

  const handlePasswordModalClose = () => {
    setShowChangePassword(false);
    setNewPassword("");
    setPasswordError("");
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    if (!newPassword || newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }
    
    // Show note modal before proceeding with password change
    setCurrentActionType("support-change-password");
    setPendingAction(() => () => proceedWithPasswordChange());
    setIsCommentModalOpen(true);
  };

  const proceedWithPasswordChange = async () => {
    try {
      const response = await resetUserPassword({ userId: user_id, password: newPassword }).unwrap();
      if (response.success === false) {
        setPasswordError("Something went wrong!");
      } else {
        toast.success("Password changed successfully.", {
          position: "bottom-center",
        });
        setShowChangePassword(false);
      }
    } catch (e) {
      setPasswordError("Failed to change password.");
    }
  };

  const handleSubmitCode = async (e) => {
    e.preventDefault();
    if (!code) {
      toast.error("Please enter the code.", { position: "bottom-center" });
      return;
    }
    
    // Show note modal for TV code submission
    setCurrentActionType("support-tv-code-login");
    setPendingAction(() => () => proceedWithCodeSubmission());
    setIsCommentModalOpen(true);
  };

  const proceedWithCodeSubmission = async () => {
    try {
      const response = await loginUserWithTvCode({ code, userId: user_id }).unwrap();
      if (response?.success) {
        toast.success(response?.message || "Code verified!", {
          position: "bottom-center",
        });
        setCode("");
      } else {
        toast.error(response?.message || "Invalid code.", {
          position: "bottom-center",
        });
      }
    } catch {
      toast.error("Failed to verify code.", { position: "bottom-center" });
    }
  };

  const handleContactClick = () => {
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  const handleResetPassword = async () => {
    if (!user_id) return;
    
    setCurrentActionType("support-send-reset-link");
    setPendingAction(() => () => proceedWithPasswordReset());
    setIsCommentModalOpen(true);
  };

  const proceedWithPasswordReset = async () => {
    try {
      await sendPasswordResetLink({ user_id, user_email: email }).unwrap();
      toast.success("Password reset link sent successfully.", {
        position: "bottom-center",
      });
    } catch (e) {
      toast.error("Failed to send password reset link.", {
        position: "bottom-center",
      });
    }
  };

  const handleSendLoginEmail = async () => {
    if (!user_id) return;
    
    setCurrentActionType("support-send-login-email");
    setPendingAction(() => () => proceedWithLoginEmail());
    setIsCommentModalOpen(true);
  };

  const proceedWithLoginEmail = async () => {
    try {
      await sendUserLoginRequest(user_id).unwrap();
      toast.success("Login email sent successfully.", {
        position: "bottom-center",
      });
    } catch (e) {
      toast.error("Failed to send login email.", { position: "bottom-center" });
    }
  };

  const handleResetAllDevices = () => {
    setShowResetAllModal(true);
  };

  const handleConfirmResetAllDevices = async () => {
    setShowResetAllModal(false);
    if (!user_id) return;
    
    // Show note modal before proceeding with device reset
    setCurrentActionType("support-reset-all-devices");
    setPendingAction(() => () => proceedWithDeviceReset());
    setIsCommentModalOpen(true);
  };

  const proceedWithDeviceReset = async () => {
    try {
      const response = await resetDeviceAll(user_id).unwrap();
      if (response.success) {
        toast.success("All devices reset successfully.", {
          position: "bottom-center",
        });
      } else {
        toast.error("Failed to reset devices.", {
          position: "bottom-center",
        });
      }
    } catch (e) {
      toast.error("Error resetting devices.", {
        position: "bottom-center",
      });
    }
  };

  const handleManageStreaks = () => {
    setShowStreakManagement(true);
  };

  const handleRecoverUser = () => {
    setShowRecoveryConfirmModal(true);
  };

  const handleRecoveryConfirm = async () => {
    setShowRecoveryConfirmModal(false);
    setCurrentActionType("support-user-recovery");
    setPendingAction(() => () => proceedWithUserRecovery());
    setIsCommentModalOpen(true);
  };

  const proceedWithUserRecovery = async () => {
    try {
      const response = await recoverUser(user_id).unwrap();
      if (response.success) {
        toast.success(response.message || "User recovery initiated successfully.", {
          position: "bottom-center",
        });
        
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error(response.message || "Failed to initiate user recovery.", {
          position: "bottom-center",
        });
      }
    } catch (error) {
      console.error('User recovery error:', error);
      toast.error("An error occurred while processing the recovery request.", {
        position: "bottom-center",
      });
    }
  };

    const handleImpersonate = async () => {
    if (!user_id) return;
    try {
      const response = await service.getUserImpersonateUrl(user_id);
      if (response.success == false) {
        toast.error("Failed to impersonate user.");
        return;
      }
      const url = response.data?.redirect_url;
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.error("Failed to impersonate user.");
      }
    } catch (error) {
      console.log('Impersonation error:', error);
      toast.error("An error occurred while trying to impersonate the user.");
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="section-heading">User Support</h4>
      </div>

      {/* Contact Customer */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Contact customer</h5>
              <p className="text-muted mb-0">Send an email to {displayEmail}</p>
            </Col>
            <Col xs="auto">
              <Button
                color="primary"
                onClick={handleContactClick}
                disabled={!email}
              >
                Contact
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Reset Password */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Reset password</h5>
              <p className="text-muted mb-0">Reset the password for {displayEmail}</p>
            </Col>
            <Col xs="auto" className="d-flex gap-2 flex-wrap align-items-center">
              <Button
                style={{minWidth:180}}
                color="primary"
                onClick={handleChangePassword}
                disabled={!email}
              >
                Change password
              </Button>
              <Button
                className="ml-2"
                style={{minWidth:180,height:'min-content'}}
                onClick={handleResetPassword}
                disabled={!email}
              >
                Send Reset Link
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Send Login Email */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Send log in email</h5>
              <p className="text-muted mb-0">Send log in email to {displayEmail}</p>
            </Col>
            <Col xs="auto">
              <Button
                color="primary"
                onClick={handleSendLoginEmail}
                disabled={!email}
              >
                Send
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Login Using Code */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Login using code</h5>
              <p className="text-muted mb-0">Enter the TV login code.</p>
            </Col>
            <Col xs="auto" className="d-flex gap-2 align-items-center flex-wrap">
              <Input
                type="text"
                placeholder="Enter code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                style={{ width: "160px", marginRight: "8px" }}
              />
              <Button
                color="primary"
                onClick={handleSubmitCode}
                disabled={!email || !user_id}
              >
                Submit
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Reset User Devices */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Reset User Devices</h5>
              <p className="text-muted mb-0">Reset all devices for {displayEmail}</p>
            </Col>
            <Col xs="auto">
              <Button
                color="primary"
                onClick={handleResetAllDevices}
                disabled={!user_id}
              >
                Reset All
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Manage User Streaks */}
      {getPublisherConfigInfo("calendar_needed") === "True" && (
        <Card className="customCard">
          <CardBody>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-1">Manage User Streaks</h5>
                <p className="text-muted mb-0">View and modify streak data for {displayEmail}</p>
              </Col>
              <Col xs="auto">
                <Button
                  style={{minWidth:180}}
                  color="primary"
                  onClick={handleManageStreaks}
                  disabled={!user_id}
                >
                  Manage Streaks
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* Recover User Account */}
      {userDelete === 0 && (
        <Card className="customCard">
          <CardBody>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-1">Recover User Account</h5>
                <p className="text-muted mb-0">Restore this deleted user account: {displayEmail}</p>
              </Col>
              <Col xs="auto">
                <Button
                  color="primary"
                  onClick={handleRecoverUser}
                >
                  Recover User
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* Impersonate User */}
      <Card className="customCard">
        <CardBody>
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-1">Impersonate User</h5>
              <p className="text-muted mb-0">View the website as {displayEmail}</p>
            </Col>
            <Col xs="auto">
              <Button
                color="primary"
                onClick={handleImpersonate}
                disabled={!user_id}
              >
                Impersonate
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Modal isOpen={showChangePassword} toggle={handlePasswordModalClose}>
        <ModalHeader toggle={handlePasswordModalClose}>
          Change Password
        </ModalHeader>
        <Form onSubmit={handlePasswordSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="newPassword">New Password</Label>
              <Input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
              {passwordError && (
                <div style={{ color: "red", fontStyle: "italic" }}>
                  {passwordError}
                </div>
              )}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handlePasswordModalClose}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Submit
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Modal isOpen={showResetAllModal} toggle={() => setShowResetAllModal(false)}>
        <ModalHeader toggle={() => setShowResetAllModal(false)}>
          Reset All Devices
        </ModalHeader>
        <ModalBody>
          Are you sure you want to reset all devices for this user?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowResetAllModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleConfirmResetAllDevices}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
      
      <AddCommentModal
        isOpen={isCommentModalOpen}
        toggle={() => setIsCommentModalOpen(false)}
        onSubmit={handleCommentSubmit}
        actionType={currentActionType}
      />
      
      <StreakManagementModal
        isOpen={showStreakManagement}
        toggle={() => setShowStreakManagement(false)}
        userEmail={email}
        userId={user_id}
      />
      
      <Modal isOpen={showRecoveryConfirmModal} toggle={() => setShowRecoveryConfirmModal(false)}>
        <ModalHeader toggle={() => setShowRecoveryConfirmModal(false)}>
          Confirm User Recovery
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to recover the account for: {displayEmail}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowRecoveryConfirmModal(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleRecoveryConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UserSupport;